import { useEffect, useRef, useState } from 'react'

import Callout from '@components/Callout/Callout'
import DialogTitle from '@components/mui/DialogTitle'
import SearchInput from '@components/SearchInput/SearchInput'
import { faFaceSadSweat, faLightbulb, faUsers } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	LinearProgress,
	List,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import { useInView } from 'react-intersection-observer'

import DialogListItem from './DialogListItem'

type UIProps = {
	name: string
	open: boolean
	searchTerm: string
	setSearchTerm: (value: string) => void
	onClose: () => void
	onSave: (results: CategorySearchResult[]) => void
	category: string
	results: CategorySearchResult[] | null
	isLoading: boolean
	isRefetching: boolean
	isFetchingNextPage: boolean
	hasNextPage: boolean
	fetchNextPage: () => void
	inputTip: string
	getPrimaryText: (item: CategorySearchResult) => string
	getSecondaryText: (item: CategorySearchResult) => string | null
	getPrimaryCaption: (item: CategorySearchResult) => string | null
	getSecondaryCaption: (item: CategorySearchResult) => string | null
	errorMessage?: string | null
}

/**
 * TreeInputDialogUI
 * UI for TreeInputDialog components, handling display and selection of tree items.
 * Accepts props from RHFTreeInput custom DialogComponent which handles data fetching.
 *
 * RHFTreeInput props:
 * @param {string} name - The form field name registered with RHF.
 * @param {boolean} open - Whether the dialog is open.
 * @param {function} onClose - Function to close the dialog.
 * @param {function} onSave - Function to save selected results.
 * DialogComponent props:
 * @param {string} searchTerm - Current search term.
 * @param {function} setSearchTerm - Function to update the search term.
 * @param {string} inputTip - Tip text displayed above the search input.
 * @param {string} category - Category of items being selected (e.g., 'categories', 'distributors').
 * @param {function} getPrimaryText - Function to get primary text for each result.
 * @param {function} getSecondaryText - Function to get secondary text for each result.
 * @param {function} getPrimaryCaption - Function to get primary caption for each result.
 * @param {function} getSecondaryCaption - Function to get secondary caption for each result.
 * Data fetching props:
 * @param {CategorySearchResult[] | null} results - Search results to display.
 * @param {boolean} isLoading - Whether data is currently loading.
 * @param {boolean} isRefetching - Whether data is currently being refetched.
 * @param {boolean} isFetchingNextPage - Whether the next page of data is being fetched.
 * @param {boolean} hasNextPage - Whether there is a next page of data to fetch.
 * @param {function} fetchNextPage - Function to fetch the next page of data.
 * @param {string | null} errorMessage - Error message to display, if any.
 */

export default function TreeInputDialogUI({
	name,
	open,
	onClose,
	onSave,
	searchTerm,
	setSearchTerm,
	results,
	isLoading,
	isRefetching,
	isFetchingNextPage,
	hasNextPage,
	fetchNextPage,
	inputTip,
	category,
	getPrimaryText,
	getSecondaryText,
	getPrimaryCaption,
	getSecondaryCaption,
	errorMessage = null,
}: UIProps) {
	const [checked, setChecked] = useState([])
	const { ref: nextPageRef, inView } = useInView()
	const theme = useTheme()
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

	// Solution found online for handling autofocus issue in Dialog
	// I believe issue stems from Dialog keepMounted property
	const ref = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (ref.current && open) {
			const inputEl: HTMLInputElement = ref?.current?.querySelector?.(`#${name}`)
			// Without timeout '/' is immediately typed into field when hotkey is used to open
			if (inputEl) {
				const timeoutId = setTimeout(() => inputEl.focus(), 0)
				return () => clearTimeout(timeoutId)
			}
		}
	}, [open, name])

	useEffect(() => {
		if (inView && hasNextPage) fetchNextPage()
	}, [inView, fetchNextPage, hasNextPage])

	const handleSearchChange = (value: string) => {
		setSearchTerm(value)
	}

	const handleToggle = (item) => () => {
		const currentIndex = checked.indexOf(item)
		const newChecked = [...checked]

		if (currentIndex === -1) {
			newChecked.push(item)
		} else {
			newChecked.splice(currentIndex, 1)
		}

		setChecked(newChecked)
	}

	return (
		<Dialog
			closeAfterTransition={false}
			open={open}
			ref={ref}
			scroll="paper"
			keepMounted
			fullScreen={fullScreen}
			fullWidth
			onClose={onClose}
			sx={{
				'& .MuiDialog-container': {
					alignItems: 'flex-start',
				},
			}}
		>
			{isRefetching && <LinearProgress color="secondary" />}
			<DialogTitle
				onClose={() => {
					setChecked([])
					onClose()
				}}
			>
				{category.charAt(0).toUpperCase() + category.slice(1)}
			</DialogTitle>
			<Stack
				sx={{
					px: 3,
					pb: 2,
				}}
				gap={2}
			>
				{errorMessage ? (
					<Alert severity="error">{errorMessage}</Alert>
				) : (
					<Callout icon={<FontAwesomeIcon icon={faLightbulb} />}>{inputTip}</Callout>
				)}

				<SearchInput
					id={name}
					error={!!searchTerm && searchTerm.length < 2}
					errorText="Type at least 2 characters to search!"
					value={searchTerm}
					onChange={handleSearchChange}
					searchOnEnter
					sx={{ position: 'sticky' }}
				/>
			</Stack>
			<DialogContent sx={{ pt: 0 }}>
				<Stack>
					{!results && !isLoading && (
						<Stack mt={2} gap={2} alignItems="center">
							<FontAwesomeIcon color={theme.palette.primary.main} size="2x" icon={faUsers} />
							<Typography>Type a value and press &quot;enter&quot; to search</Typography>
						</Stack>
					)}
					{isLoading && (
						<Box display="flex" justifyContent="center">
							<CircularProgress color="secondary" />
						</Box>
					)}
					{results && !results.length && (
						<Stack mt={2} gap={2} alignItems="center">
							<FontAwesomeIcon color={theme.palette.primary.main} size="2x" icon={faFaceSadSweat} />
							<Typography>Sorry, we couldn&apos;t find a match</Typography>
						</Stack>
					)}
					{results && results.length > 0 && (
						<List sx={{ width: '100%' }}>
							{results.map((item, i) => {
								const labelId = `checkbox-list-label-${item.id}`
								const indexToFetchNextPage = Math.floor(results.length * 0.7)
								const ref = i === indexToFetchNextPage ? nextPageRef : null
								const primaryText = getPrimaryText(item)
								const secondaryText = getSecondaryText(item)
								const primaryCaption = getPrimaryCaption(item)
								const secondaryCaption = getSecondaryCaption(item)
								return (
									<DialogListItem
										key={item.id}
										ref={ref}
										item={item}
										searchTerm={searchTerm}
										checked={checked}
										handleToggle={handleToggle}
										labelId={labelId}
										primaryText={primaryText}
										secondaryText={secondaryText}
										primaryCaption={primaryCaption}
										secondaryCaption={secondaryCaption}
									/>
								)
							})}
						</List>
					)}
					{isFetchingNextPage && (
						<CircularProgress sx={{ alignSelf: 'center' }} color="secondary" />
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					disabled={!checked.length}
					onClick={() => {
						onSave(checked)
						setChecked([])
						onClose()
					}}
					variant="contained"
				>
					Add {category}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
